import { useState } from "react";
import { AlertCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/Button";
import ProfileStep from "./ProfileStep";
import WorkplaceStep from "./WorkplaceStep";
import DepartmentStep from "./DepartmentStep";
import ReviewStep from "./ReviewStep";
import { OTHER_DEPARTMENT_ID, type Department, type Profile, type RequestPayload, type Workplace } from "./types";

type Errors = Record<string, string | string[]>;

type Props = {
  orgName: string;
  profile: Profile;
  workplaces: Workplace[];
  departments: Department[];
  submitting?: boolean;
  errors?: Errors;
  onSubmit: (payload: RequestPayload) => void;
};

type Step = 0 | 1 | 2 | 3;

const STEPS = ["Your account", "Where you work", "What you do", "Review"];

// Which step owns each server-side error field, so a failed submit reopens the
// flow on the first step the user actually needs to fix.
const ERROR_STEP: Record<string, Step> = {
  name: 0,
  password: 0,
  password_confirmation: 0,
  org_node_id: 1,
  requested_role_id: 2,
  role_note: 2,
};

const firstErroredStep = (errors?: Errors): Step =>
  Object.keys(errors ?? {}).reduce<Step>((min, key) => {
    const step = ERROR_STEP[key];
    return step !== undefined && step < min ? step : min;
  }, 3);

// Self-service access request: account → workplace → department → review →
// submit. Approval-first, so submitting only files a request. Pure UI.
// Layout: centered welcome header + progress, one white card per step with
// the question, the options, and the actions inside it.
export default function RequestAccessFlow({ orgName, profile, workplaces, departments, submitting = false, errors, onSubmit }: Props) {
  const hasErrors = Object.keys(errors ?? {}).length > 0;
  const [step, setStep] = useState<Step>(() => (hasErrors ? firstErroredStep(errors) : 0));
  const [name, setName] = useState(profile.name);
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [workplaceId, setWorkplaceId] = useState<string | null>(null);
  const [departmentId, setDepartmentId] = useState<string | null>(null);
  const [otherText, setOtherText] = useState("");

  const workplace = workplaces.find((w) => w.id === workplaceId) ?? null;
  const department = departments.find((d) => d.id === departmentId) ?? null;
  const otherSelected = departmentId === OTHER_DEPARTMENT_ID;
  const departmentLabel = otherSelected ? otherText.trim() : (department?.name ?? "");

  const stepValid =
    step === 0 ? Boolean(name.trim()) && password.length >= 8 && password === passwordConfirmation
    : step === 1 ? Boolean(workplaceId)
    : step === 2 ? Boolean(departmentId) && (!otherSelected || otherText.trim().length > 0)
    : true;

  const submit = () => {
    if (!workplace || !departmentLabel) return;
    onSubmit({
      org_node_id: workplace.id,
      requested_role_id: otherSelected ? null : departmentId,
      role_note: departmentLabel,
      name,
      password,
      password_confirmation: passwordConfirmation,
    });
  };

  return (
    <div className="min-h-dvh bg-muted/40 px-4 py-10 sm:py-14">
      <div className="mx-auto w-full max-w-xl">
        {/* Progress + welcome — persistent across steps */}
        <ol className="flex items-center gap-2" aria-label="Progress">
          {STEPS.map((label, i) => (
            <li key={label} className="flex flex-1">
              <span className={i <= step ? "h-1 flex-1 rounded-full bg-primary" : "h-1 flex-1 rounded-full bg-border"} />
            </li>
          ))}
        </ol>
        <p className="mt-1.5 text-xs text-muted-foreground">
          Step {step + 1} of {STEPS.length} — {STEPS[step]}
        </p>

        <div className="mt-5 space-y-1.5">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Welcome to {orgName}!</h1>
          <p className="text-sm text-muted-foreground">
            A few quick questions so an admin can set up your access.
          </p>
        </div>

        {/* Step card — fixed height so the pinned action bar never moves;
            step content scrolls internally */}
        <div className="mt-6 flex h-[min(72dvh,640px)] flex-col overflow-hidden rounded-lg border border-border bg-card shadow-sm">
          <div className="min-h-0 flex-1 overflow-y-auto p-6">
          {hasErrors && (
            <div className="mb-5 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2.5" role="alert">
              <AlertCircle size={16} className="mt-0.5 shrink-0 text-destructive" />
              <div className="space-y-0.5 text-sm text-destructive">
                {Object.values(errors ?? {}).flat().map((msg) => (
                  <p key={msg}>{msg}</p>
                ))}
              </div>
            </div>
          )}
          {step === 0 && (
            <ProfileStep
              email={profile.email}
              name={name}
              password={password}
              passwordConfirmation={passwordConfirmation}
              onNameChange={setName}
              onPasswordChange={setPassword}
              onPasswordConfirmationChange={setPasswordConfirmation}
            />
          )}
          {step === 1 && (
            <WorkplaceStep workplaces={workplaces} selectedId={workplaceId} onSelect={setWorkplaceId} />
          )}
          {step === 2 && (
            <DepartmentStep
              departments={departments}
              selectedId={departmentId}
              otherText={otherText}
              onSelect={setDepartmentId}
              onOtherTextChange={setOtherText}
            />
          )}
          {step === 3 && workplace && (
            <ReviewStep
              name={name}
              email={profile.email}
              workplace={workplace}
              departmentLabel={departmentLabel}
              onEdit={setStep}
            />
          )}

          </div>

          {/* Action bar — pinned to the card bottom, Back left / Continue right */}
          <div className="flex shrink-0 items-center justify-between border-t border-border px-6 py-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setStep((s) => (s > 0 ? ((s - 1) as Step) : s))}
              disabled={step === 0}
            >
              Back
            </Button>
            {step < 3 ? (
              <Button type="button" disabled={!stepValid} onClick={() => setStep((s) => (s + 1) as Step)}>
                Continue
                <ArrowRight size={14} />
              </Button>
            ) : (
              <Button type="button" disabled={submitting} onClick={submit}>
                {submitting ? "Submitting…" : "Submit request"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
